import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useConfirm } from "@/lib/confirm";
import { Search, CheckCircle2, AlertTriangle, GraduationCap, Ticket, PartyPopper, Download, Pencil, UserPlus, Trash2 } from "lucide-react";
import Pill from "@/admin/registrations/Pill";
import WalkInModal from "@/admin/registrations/WalkInModal";
import PaymentEditModal from "@/admin/registrations/PaymentEditModal";

const TYPE_META = {
  class:    { label: "Class",    icon: GraduationCap, color: "text-red-400" },
  workshop: { label: "Workshop", icon: Ticket,        color: "text-amber-400" },
  event:    { label: "Event",    icon: PartyPopper,   color: "text-emerald-400" },
};

export default function AllRegistrations() {
  const { user } = useAuth();
  const canEditPayment = user?.role === "manager" || user?.role === "super_admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ type: "", payment_status: "", q: "", program: "", location: "" });
  const [editing, setEditing] = useState(null);
  const [walkInOpen, setWalkInOpen] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.type) params.set("type", filters.type);
      if (filters.payment_status) params.set("payment_status", filters.payment_status);
      const r = await api.get(`/admin/all-registrations?${params}`);
      setRows(r.data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [filters.type, filters.payment_status]);
  useEffect(() => { load(); }, [load]);

  const deleteRow = async (r) => {
    const ok = await confirm({
      title: `Delete ${r.full_name || "this registration"}?`,
      description: `Removes their spot from ${r.program_name || r.type}. Refunds are not processed — handle Stripe refunds separately if needed.`,
      danger: true, confirmLabel: "Delete registration",
    });
    if (!ok) return;
    const url =
      r.type === "class"    ? `/admin/registrations/${r.id}` :
      r.type === "workshop" ? `/admin/workshops/${r.program_id}/registrations/${r.id}` :
      r.type === "event"    ? `/admin/events/${r.program_id}/registrations/${r.id}` :
      null;
    if (!url) { toast.error("Unknown registration type"); return; }
    try {
      await api.delete(url);
      toast.success("Registration deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const counts = useMemo(() => {
    const c = { all: rows.length, class: 0, workshop: 0, event: 0, paid: 0, owing: 0 };
    rows.forEach(r => {
      c[r.type] = (c[r.type] || 0) + 1;
      if (r.fully_paid) c.paid++; else if (r.amount_owed > 0) c.owing++;
    });
    return c;
  }, [rows]);

  const programOptions = useMemo(() => Array.from(new Set(rows.map(r => (r.program_name || "").trim()).filter(Boolean))).sort(), [rows]);
  const locationOptions = useMemo(() => Array.from(new Set(rows.map(r => (r.program_detail || "").split("·").pop().trim()).filter(Boolean).filter(l => l.length < 60))).sort(), [rows]);

  const localFiltered = useMemo(() => {
    const n = filters.q.toLowerCase();
    return rows.filter(r => {
      if (filters.program && (r.program_name || "") !== filters.program) return false;
      if (filters.location && !(r.program_detail || "").toLowerCase().includes(filters.location.toLowerCase())) return false;
      if (!n) return true;
      return (r.full_name || "").toLowerCase().includes(n) ||
             (r.email || "").toLowerCase().includes(n) ||
             (r.phone || "").toLowerCase().includes(n) ||
             (r.program_name || "").toLowerCase().includes(n);
    });
  }, [rows, filters.q, filters.program, filters.location]);

  const exportCsv = () => {
    const header = ["Type","Name","Email","Phone","Program","When/Where","Payment","Amount Paid","Expected","Owing","Email Opt-in","Photo Consent","Created"];
    const csv = [header, ...localFiltered.map(r => [
      r.type, r.full_name || "", r.email || "", r.phone || "", r.program_name || "", r.program_detail || "",
      r.payment_status || "", r.amount_paid ?? 0, r.program_expected_amount ?? 0, r.amount_owed ?? 0,
      r.email_opt_in ? "yes" : "no", r.photo_consent ? "yes" : "no", r.created_at || ""
    ])].map(row => row.map(v => `"${String(v).replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `registrations-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 sm:p-8 max-w-[1600px]">
      <div className="mb-6">
        <div className="eyebrow mb-2">All registrations</div>
        <h1 className="font-display text-5xl uppercase">Registrations</h1>
        <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Every class, workshop, and event registration in one feed. Filter by type or payment status. Amounts owed are calculated against the program's list price so you can spot underpayments at a glance.</p>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap" data-testid="stat-pills">
        <Pill label="All" value={counts.all} active={!filters.type} onClick={() => setFilters(f => ({...f, type: ""}))} testId="pill-all"/>
        <Pill label="Classes" value={counts.class || 0} icon={GraduationCap} active={filters.type === "class"} onClick={() => setFilters(f => ({...f, type: "class"}))} testId="pill-class"/>
        <Pill label="Workshops" value={counts.workshop || 0} icon={Ticket} active={filters.type === "workshop"} onClick={() => setFilters(f => ({...f, type: "workshop"}))} testId="pill-workshop"/>
        <Pill label="Events" value={counts.event || 0} icon={PartyPopper} active={filters.type === "event"} onClick={() => setFilters(f => ({...f, type: "event"}))} testId="pill-event"/>
        <div className="ml-auto flex gap-2">
          <Pill label="Fully paid" value={counts.paid} icon={CheckCircle2} tint="emerald" active={filters.payment_status === "paid"} onClick={() => setFilters(f => ({...f, payment_status: f.payment_status === "paid" ? "" : "paid"}))} testId="pill-paid"/>
          <Pill label="Owing" value={counts.owing} icon={AlertTriangle} tint="amber" testId="pill-owing"/>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
          <input value={filters.q} onChange={e => setFilters(f => ({...f, q: e.target.value}))} placeholder="Search name, email, phone, program…" className="w-full bg-black border border-white/10 pl-9 pr-3 py-2 text-sm" data-testid="registrations-search"/>
        </div>
        <select value={filters.payment_status} onChange={e => setFilters(f => ({...f, payment_status: e.target.value}))} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="payment-filter">
          <option value="">All payments</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="manual">Manual</option>
          <option value="refunded">Refunded</option>
          <option value="comp">Comp</option>
        </select>
        <select value={filters.program} onChange={e => setFilters(f => ({...f, program: e.target.value}))} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="program-filter">
          <option value="">All programs</option>
          {programOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.location} onChange={e => setFilters(f => ({...f, location: e.target.value}))} className="bg-black border border-white/10 px-3 py-2 text-sm" data-testid="location-filter">
          <option value="">All locations</option>
          {locationOptions.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        {canEditPayment && (
          <button onClick={() => setWalkInOpen(true)} className="btn-red !py-2 !px-3 !text-xs flex items-center gap-1" data-testid="add-walkin-btn"><UserPlus size={12}/>Register walk-in</button>
        )}
        <button onClick={exportCsv} className="btn-ghost !py-2 !px-3 !text-xs flex items-center gap-1" data-testid="export-registrations"><Download size={12}/>Export CSV</button>
      </div>

      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-zinc-500 uppercase tracking-widest text-[10px]">
              <th className="text-left px-3 py-3">Type</th>
              <th className="text-left px-3 py-3">Attendee</th>
              {canEditPayment && <th className="text-left px-3 py-3">Contact</th>}
              <th className="text-left px-3 py-3">Program</th>
              <th className="text-left px-3 py-3">When / where</th>
              <th className="text-left px-3 py-3">Payment</th>
              <th className="text-left px-3 py-3">Paid</th>
              <th className="text-left px-3 py-3">Owing</th>
              <th className="text-left px-3 py-3">Updates</th>
              <th className="text-left px-3 py-3">Photo OK</th>
              <th className="text-left px-3 py-3">Created</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={canEditPayment ? 11 : 10} className="text-center py-16 text-zinc-500">Loading…</td></tr>}
            {!loading && localFiltered.map(r => {
              const meta = TYPE_META[r.type] || {};
              const Icon = meta.icon;
              return (
                <tr key={`${r.type}-${r.id}`} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`reg-row-${r.id}`}>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest ${meta.color || "text-zinc-400"}`}>
                      {Icon && <Icon size={12}/>}{meta.label || r.type}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-white">{r.full_name || "—"} {r.manual_entry && <span className="text-[9px] uppercase tracking-widest text-red-400 ml-1">manual</span>}</div>
                    {r.notes && <div className="text-[10px] text-zinc-500 mt-1">{r.notes}</div>}
                  </td>
                  {canEditPayment && (
                    <td className="px-3 py-3">
                      <div className="text-zinc-300 text-xs">{r.email || "—"}</div>
                      <div className="text-zinc-500 text-xs">{r.phone || "—"}</div>
                    </td>
                  )}
                  <td className="px-3 py-3">
                    <div className="text-white">{r.program_name || "—"}</div>
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-400">{r.program_detail || "—"}</td>
                  <td className="px-3 py-3">
                    <span className={`text-xs px-2 py-1 border ${r.fully_paid ? "border-emerald-500/40 text-emerald-400" : (r.payment_status === "refunded" ? "border-zinc-500/40 text-zinc-400" : "border-amber-500/40 text-amber-400")}`}>
                      {r.payment_status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-3">${Number(r.amount_paid ?? 0).toFixed(2)}</td>
                  <td className="px-3 py-3">
                    {r.amount_owed > 0
                      ? <span className="text-amber-400 font-bold">${Number(r.amount_owed).toFixed(2)}</span>
                      : <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={12}/>settled</span>}
                  </td>
                  <td className="px-3 py-3 text-xs" data-testid={`optin-${r.id}`}>
                    {r.email_opt_in
                      ? <span className="text-emerald-400" title="Opted in to reminders & updates">✓ yes</span>
                      : <span className="text-zinc-600" title="Not opted in">— no</span>}
                  </td>
                  <td className="px-3 py-3 text-xs" data-testid={`photo-consent-${r.id}`}>
                    {r.photo_consent
                      ? <span className="text-emerald-400" title="Photo & video consent given">✓ yes</span>
                      : <span className="text-amber-400 font-bold" title="NO photo/video consent">✗ no</span>}
                  </td>
                  <td className="px-3 py-3 text-xs text-zinc-500">{(r.created_at || "").slice(0,10)}</td>
                  <td className="px-3 py-3 text-right">
                    {canEditPayment && (
                      <div className="flex justify-end gap-1">
                        <button onClick={() => setEditing(r)} className="text-zinc-400 hover:text-white p-1" title="Edit payment" data-testid={`edit-payment-${r.id}`}><Pencil size={14}/></button>
                        <button onClick={() => deleteRow(r)} className="text-zinc-400 hover:text-red-500 p-1" title="Delete registration" data-testid={`delete-reg-${r.id}`}><Trash2 size={14}/></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {!loading && localFiltered.length === 0 && (
              <tr><td colSpan={11} className="text-center py-16 text-zinc-500">No registrations match this filter.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && <PaymentEditModal row={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {walkInOpen && <WalkInModal onClose={() => setWalkInOpen(false)} onDone={() => { setWalkInOpen(false); load(); }} />}
    </div>
  );
}
